import React, { useEffect, useMemo } from "react";
import Stack from "../../../../components/lib/Stack";
import Text from "../../../../components/lib/Text";
import Button from "../../../../components/lib/Button";
import Highchart from "../../../../components/charts/Highchart";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";
import { BellCurveBillRates_savedsearch$key } from "./__generated__/BellCurveBillRates_savedsearch.graphql";
import { getBellCurveConfig } from "./bellcurve";
import { useSearchResultsStore } from "../../../../stores/SearchResults";
import Inline from "../../../../components/lib/Inline";
import Tooltip from "../../../../components/lib/Tooltip";
import Icon from "../../../../components/lib/Icon";

const fragment = graphql`
  fragment BellCurveBillRates_savedsearch on SaveSearchNode {
    id
    currency {
      id
      iso @required(action: NONE)
    }
    rates {
      rawBillRates @required(action: NONE)
      perDiem @required(action: NONE)
      rawMin @required(action: NONE)
      rawAvg @required(action: NONE)
      rawMid @required(action: NONE)
      rawMax @required(action: NONE)
    }
  }
`;

type Props = {
  isContract: boolean;
  hideToggles?: boolean;
  savedsearch: BellCurveBillRates_savedsearch$key;
};

export function BellCurveBillRates({ savedsearch, isContract, hideToggles }: Props) {
  const data = useFragment(fragment, savedsearch);
  const rateFrequency = useSearchResultsStore((s) => s.rateFrequency);
  const multiplier = useSearchResultsStore(
    (s) => s.rateFrequencyMultipliers[s.rateFrequency]
  );
  const setRateFrequency = useSearchResultsStore((s) => s.setRateFrequency);
  const fromCurrency = data?.currency?.iso || "USD";
  const toCurrency = useSearchResultsStore((s) => s.currency);
  const calculateRate = useSearchResultsStore((s) => s.calculateRate);

  const initializeRates = useSearchResultsStore((s) => s.initializeRates);

  useEffect(() => {
    initializeRates();
  }, [initializeRates]);

  const formatRate = calculateRate(
    fromCurrency,
    toCurrency,
    isContract ? rateFrequency : "Annually",
    true
  );

  const perDiem = data?.rates?.perDiem || null;

  const options = useMemo(() => {
    if (!data?.rates || multiplier <= 0) return {};

    return getBellCurveConfig(data?.rates.rawBillRates as number[], (rate?: number) => {
      return calculateRate(
        fromCurrency,
        toCurrency,
        isContract ? rateFrequency : "Annually",
        false
      )(rate, true, isContract);
    });
  }, [
    data,
    multiplier,
    calculateRate,
    fromCurrency,
    toCurrency,
    rateFrequency,
    isContract,
  ]);

  if (!data || !data.rates) {
    console.error("Healthcare Bell Curve: Missing data");
    return null;
  }

  return (
    <Stack fill nogap css={{ alignItems: "flex-start", position: "relative" }}>
      <Stack
        css={{
          alignItems: "flex-start",
          justifyContent: "flex-start",
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 2,
        }}
      >
        <Stack nogap css={{ alignItems: "flex-start" }}>
          <Text noMargin as="h4">
            Market Rates
          </Text>
          <Text color="primary">
            Normal distribution of {data.rates.rawBillRates.length} results
            <Tooltip
              arrowProps={{ offset: 5 }}
              content={
                <Stack css={{ alignItems: "flex-start", maxWidth: "$68" }}>
                  <Text>
                    The 'Market Rates' displayed to the left of the curve represent the
                    precise market value based on the search criteria. The bell curve
                    represents a normal distribution of the underlying observations
                    included in this result.
                  </Text>
                  <Text>
                    The display and data points included in the graphic are for guidance,
                    and may not represent actual observations due to the normalization of
                    the data sample.
                  </Text>
                </Stack>
              }
            >
              <Icon icon="info-circle" css={{ marginLeft: "$2" }} />
            </Tooltip>
          </Text>
        </Stack>

        <Stack css={{ alignItems: "flex-start", paddingBottom: "$2" }} nogap>
          <Text color="primary" size="sm">
            Average Bill Rate
          </Text>
          <Text noMargin as="h2">
            {formatRate(data.rates.rawAvg, true, isContract)}
          </Text>
          <Stack nogap css={{ alignItems: "flex-start" }}>
            <Text color="primary" size="sm">
              Minimum:{" "}
              <Text color="dark">{formatRate(data.rates.rawMin, true, isContract)}</Text>
            </Text>
            <Text color="primary" size="sm">
              Maximum:{" "}
              <Text color="dark">{formatRate(data.rates.rawMax, true, isContract)}</Text>
            </Text>
          </Stack>
          {perDiem && (
            <Text color="positive" size="sm">
              <Text css={{ fontWeight: "$semibold" }}>
                {formatRate(perDiem, true, false)}
              </Text>
              &nbsp;Daily allowance, <br />
              Includes food and lodging.
            </Text>
          )}
        </Stack>

        {isContract && !hideToggles && (
          <Inline>
            <Button
              onClick={() => setRateFrequency("Hourly")}
              color={rateFrequency === "Hourly" ? "brand" : "primary"}
              variant={rateFrequency === "Hourly" ? "filled" : "outlined"}
            >
              Hourly
            </Button>
            <Button
              color={rateFrequency === "Annually" ? "brand" : "primary"}
              variant={rateFrequency === "Annually" ? "filled" : "outlined"}
              onClick={() => setRateFrequency("Annually")}
            >
              Annually
            </Button>
          </Inline>
        )}
      </Stack>
      <Stack
        css={{
          gap: "0",
          justifyContent: "space-between",
          width: "$full",
          "& div": { width: "$full" },
        }}
      >
        <Highchart options={options} />
      </Stack>
      <Inline
        css={{
          width: "$full",
          justifyContent: "space-evenly",
          fontSize: "$base",
        }}
      >
        <Text size="sm" color="primary" css={{ paddingLeft: "$16 !important" }}>
          Minimum
        </Text>
        <Text size="sm" color="primary">
          Average
        </Text>
        <Text size="sm" color="primary" css={{ paddingRight: "$10 !important" }}>
          Maximum
        </Text>
      </Inline>
    </Stack>
  );
}
